"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, PhoneCall, Calendar, TrendingUp, ShieldCheck, UserCheck, Sparkles, Search, Award, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function Localseo() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = [{
    title: "Direct Consultant Access",
    desc: "You work 1-on-1 directly with me, Yassine Essaadi. No account managers or junior delegates."
  }, {
    title: "Google Map Pack Dominance",
    desc: "Optimised Google Business Profile and hyper-local citation strategies tailored for Swansea."
  }, {
    title: "Transparent & Honest Reporting",
    desc: "Clear deliverables, real ranking progress, and practical implementation without jargon."
  }, {
    title: "Tailored Swansea SEO Strategy",
    desc: "Bespoke campaigns designed around local search intent across Swansea, South Wales, and the UK."
  }];
  const localAuthorityStats = [{
    label: "Local Map Pack Focus",
    value: "Top 3 Rank Strategy"
  }, {
    label: "Direct Service Model",
    value: "100% Freelance Expert"
  }, {
    label: "Client Partner Approach",
    value: "Dedicated 1-on-1"
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseohero" data-nav="light" className="relative bg-background text-foreground py-12 md:py-20 lg:py-24 overflow-hidden border-b border-border">
      <div className="aria-hidden:true pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-20 size-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/2 -right-20 size-96 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <motion.div className="lg:col-span-7 flex flex-col items-start text-left" variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="secondary" className="px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-full bg-secondary text-secondary-foreground border border-border flex items-center gap-2 shadow-xs">
                <MapPin className="size-4 text-primary shrink-0" />
                <span>Swansea &amp; South Wales Local SEO Specialist</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.12] mb-6">
              Local SEO Consultant <span className="text-primary">in Swansea</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              I help local businesses in Swansea and across the UK capture high-intent search traffic, dominate Google Maps, and convert nearby customers. When you hire YE Consultancy, <strong className="text-foreground font-semibold">you work directly with me</strong>—Yassine Essaadi.
            </motion.p>

            <motion.div variants={itemVariants} className="w-full bg-primary/5 border-l-4 border-primary p-4 sm:p-5 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <UserCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-foreground/90 leading-normal">
                  <span className="font-semibold text-foreground">No agency bloat or outsourced tasks:</span> I personally handle your technical audits, local keyword research, Google Business Profile optimisation, and monthly strategy.
                </p>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto mb-8">
              <Button size="lg" className="rounded-md font-medium text-sm sm:text-base px-6 h-12 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="mr-2 size-4" /></SafeHydrate>
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" className="rounded-md font-medium text-sm sm:text-base px-6 h-12 border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-xs transition-all" asChild>
                <Link to="/CaseStudiesPage">
                  <TrendingUp className="mr-2 size-4 text-primary" />
                  Explore My Case Studies
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-muted-foreground mb-8">
              <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded-xs">
                <ShieldCheck className="mr-1.5 size-4 text-primary" />
                contact@ye-consultancy.com
              </a>
              <a href="tel:+447490984430" className="inline-flex items-center hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded-xs">
                <PhoneCall className="mr-1.5 size-4 text-primary" />
                +44 7490984430
              </a>
            </motion.div>

            <Separator className="my-2 mb-8 bg-border" />

            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              {trustPoints.map((point, i) => <div key={point.title} data-index={i} className="flex items-start gap-3 p-3.5 rounded-lg bg-card border border-border shadow-xs hover:border-primary/40 transition-colors">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif text-sm font-semibold text-foreground mb-0.5">
                      {point.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {point.desc}
                    </p>
                  </div>
                </div>)}
            </motion.div>

          </motion.div>

          <motion.div className="lg:col-span-5 flex flex-col gap-6" initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <div className="relative rounded-2xl bg-card border border-border p-6 shadow-xl overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <Search className="size-36 text-primary" />
              </div>

              <div className="relative z-10 flex flex-col items-center text-center">
                
                <div className="relative mb-5 group">
                  <div className="size-36 sm:size-40 rounded-full overflow-hidden border-4 border-primary/20 p-1 bg-background shadow-md">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_portrait.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_portrait.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_portrait.webp-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant in Swansea" loading="lazy" className="w-full h-full object-cover rounded-full" />
                  </div>
                  <div className="absolute bottom-1 right-1 bg-primary text-primary-foreground p-2 rounded-full shadow-lg border-2 border-background">
                    <Award className="size-4" />
                  </div>
                </div>

                <h2 className="font-serif text-xl font-bold text-foreground mb-1">
                  Yassine Essaadi
                </h2>
                <p className="text-xs sm:text-sm font-medium text-primary mb-3">
                  Independent SEO Strategy Consultant
                </p>

                <p className="text-xs text-muted-foreground leading-relaxed mb-6 max-w-xs">
                  Specialising in Local SEO, Google Map Pack optimisation, and organic growth for Swansea &amp; national businesses.
                </p>

                <div className="grid grid-cols-3 gap-2 w-full pt-4 border-t border-border">
                  {localAuthorityStats.map((stat, i) => <div key={stat.label} data-index={i} className="text-center p-2 rounded-md bg-muted/50">
                      <p className="font-serif text-xs font-bold text-foreground leading-tight mb-1">
                        {stat.value}
                      </p>
                      <p className="text-[11.5px] text-muted-foreground leading-tight">
                        {stat.label}
                      </p>
                    </div>)}
                </div>

              </div>
            </div>

            <div className="rounded-xl bg-primary text-primary-foreground p-5 shadow-lg relative overflow-hidden">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="size-5 text-accent" />
                  <span className="font-serif text-sm font-semibold">Local SEO Methodology</span>
                </div>
                <Badge variant="outline" className="text-[11.5px] text-primary-foreground border-primary-foreground/30 bg-primary-foreground/10">
                  Swansea Focused
                </Badge>
              </div>

              <p className="text-xs text-primary-foreground/90 leading-relaxed mb-4">
                Local search isn&apos;t just about keywords. I optimise your Google Business Profile, fix local technical schema, build authoritative UK citations, and target real commercial queries in Swansea.
              </p>

              <div className="flex items-center justify-between text-xs pt-2 border-t border-primary-foreground/20">
                <span className="text-primary-foreground/80 flex items-center gap-1">
                  <Star className="size-3.5 text-accent fill-accent" />
                  Tailored Swansea Strategy
                </span>
                <Link to="/LocalSEOPage" className="font-medium text-primary-foreground hover:underline inline-flex items-center gap-1 focus:outline-none">
                  Learn More <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>

          </motion.div>

        </div>
      </div>
    </section>;
}